import { isString } from 'lodash';
import {
  makeAdvancedReportsApi,
  makeApi,
  makeBenefitsApi,
} from 'src/http/api/internal/internal-http';
import { CouponsRepository } from 'src/repositories/benefits/coupons.repository';
import { ProductRepository } from 'src/repositories/products/products.repository';
import {
  couponsTypeItem,
  locationsTypeItem,
  parkingsTypeItem,
  storeTypeItem,
} from 'src/types/coupons';
// import { api } from 'src/utils/axios';
import { toBase64 } from 'src/utils/utils';
import { create } from 'zustand';

type States = {
  isLoadingCouponsList: boolean;
  isLoadingLPCouponsList: boolean;
  isLoadingCouponDetails: boolean;
  isLoadingStores: boolean;
  isLoadingCreateCoupon: boolean;
  isLoadingUpdateCoupon: boolean;
  isLoadingLocations: boolean;
  isLoadingParkings: boolean;
  isLoadingChangeLPCouponStatus: boolean;
  locations: locationsTypeItem[];
  couponsList: couponsTypeItem[];
  lpCouponsList: couponsTypeItem[];
  couponDetails: couponsTypeItem | null;
  categories: any[];
  themes: any[];
  stores: storeTypeItem[];
  parkings: parkingsTypeItem[];
};

type Actions = {
  getCouponsList: () => Promise<void>;
  getLPCouponsList: () => Promise<void>;
  getCouponDetails: (id: string) => Promise<any>;
  getLPCouponDetails: (id: string) => Promise<any>;
  getStores: () => Promise<void>;
  getLocations: () => Promise<void>;
  getCategories: () => Promise<void>;
  getThemes: () => Promise<void>;
  resetCouponDetails: () => void;
  createCoupon: (coupon: any) => Promise<void>;
  createLPCoupon: (coupon: any) => Promise<void>;
  updateCoupon: (coupon: any) => Promise<void>;
  updateLPCoupon: (coupon: any) => Promise<void>;
  approveLPCoupon: (id: string) => Promise<void>;
  refuseLPCoupon: (id: string, comment: string) => Promise<void>;
  exportData: (
    ids: string[],
    userName: string,
    options?: { isAllSelected?: boolean }
  ) => void;
  getAllParkings: () => Promise<void>;
  changeLPCouponStatus: (id: string) => Promise<void>;
};

type Coupons = Actions & States;

const initialValues = {
  isLoadingCouponsList: false,
  isLoadingLPCouponsList: false,
  isLoadingCouponDetails: false,
  isLoadingStores: false,
  isLoadingCreateCoupon: false,
  isLoadingCreateLPCoupon: false,
  isLoadingUpdateCoupon: false,
  isLoadingUpdateLPCoupon: false,
  isLoadingApproveLPCoupon: false,
  isLoadingRefuseLPCoupon: false,
  isLoadingLocations: false,
  isLoadingParkings: false,
  isLoadingChangeLPCouponStatus: false,
  locations: [],
  couponsList: [],
  lpCouponsList: [],
  categories: [],
  themes: [],
  couponDetails: null,
  stores: [],
  parkings: [],
};

const couponsRepository = new CouponsRepository(makeBenefitsApi());
const productRepository = new ProductRepository(makeBenefitsApi());
const authRepository = new CouponsRepository(makeApi());
const advancedReportsRepository = new CouponsRepository(makeAdvancedReportsApi());

export const useCouponsStore = create<Coupons>((set: any, get: any) => ({
  ...initialValues,

  getCouponsList: async () => {
    set({ isLoadingCouponsList: true });
    const response = await couponsRepository.getCouponsList();

    if (response.isLeft()) {
      set({ isLoadingCouponsList: false });
      throw new Error(response.value.message);
    } else {
      set({ couponsList: response.value, isLoadingCouponsList: false });
    }
  },

  getLPCouponsList: async () => {
    set({ isLoadingLPCouponsList: true });
    const response = await couponsRepository.getLPCouponsList();

    if (response.isLeft()) {
      set({ isLoadingLPCouponsList: false });
      throw new Error(response.value.message);
    } else {
      set({ lpCouponsList: response.value, isLoadingLPCouponsList: false });
    }
  },

  getCouponDetails: async (id) => {
    const response = await couponsRepository.getCouponDetails(id);
    set({ isLoadingCouponDetails: true });
    if (response.isLeft()) {
      return {} as couponsTypeItem;
    }
    set({ couponDetails: response.value, isLoadingCouponDetails: false });
    const { value } = response;
    return value as couponsTypeItem;
  },

  getStores: async () => {
    set({ isLoadingStores: true });
    const response = await couponsRepository.getStores();
    const storesList = response.value.map((store: any) => ({
      value: store._id,
      label: store.name,
    }));

    if (response.isLeft()) {
      set({ isLoadingStores: false });
      throw new Error(response.value.message);
    } else {
      set({ stores: storesList, isLoadingStores: false });
    }
  },

  getLPCouponDetails: async (id) => {
    const response = await couponsRepository.getCouponDetails(id);
    set({ isLoadingCouponDetails: true });
    if (response.isLeft()) {
      return {} as couponsTypeItem;
    }
    set({ couponDetails: response.value, isLoadingCouponDetails: false });
    const { value } = response;
    return value as couponsTypeItem;
  },

  changeLPCouponStatus: async (id) => {
    set({ isLoadingChangeLPCouponStatus: true });
    const response = await couponsRepository.changeLPCouponStatus(id);

    if (response.isLeft()) {
      set({ isLoadingChangeLPCouponStatus: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingChangeLPCouponStatus: false });
    }
  },

  getLocations: async () => {
    set({ isLoadingLocations: true });
    const response = await couponsRepository.getLocations();
    if (response.isLeft()) {
      set({ locations: [], isLoadingLocations: false });
      return;
    }
    const { value } = response;
    const loc = value.map((location: any) => ({
      value: location._id,
      label: location.name,
      location: location.location || location.unit || location.mall || '',
    }));
    loc.unshift({ value: 'all', label: 'Todos' });
    set({
      locations: loc,
      isLoadingLocations: false,
    });
  },

  getCategories: async () => {
    const response = await productRepository.getCategories();
    if (response.isLeft()) {
      set({ categories: [] });
      return;
    }
    const data = response.value.map((category: any) => ({
      value: category._id,
      label: category.name,
    }));
    set({
      categories: data,
    });
  },

  getThemes: async () => {
    const response = await productRepository.getThemes();
    if (response.isLeft()) {
      set({ themes: [] });
      return;
    }
    const { value } = response;
    const the = value.map((theme: any) => ({ value: theme._id, label: theme.name }));
    set({
      themes: the,
    });
  },

  resetCouponDetails: () => {
    set({ couponDetails: null });
  },

  createCoupon: async (coupon) => {
    set({ isLoadingCreateCoupon: true });

    if (coupon.image && !isString(coupon.image)) {
      try {
        const imgBase64: any = await toBase64(coupon.image);
        const res: any = await authRepository.uploadFile({
          name: coupon.image.name,
          base64: imgBase64,
        });
        coupon.image = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }
    const response = await couponsRepository.createCoupon(coupon);

    if (response.isLeft()) {
      set({ isLoadingCreateCoupon: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingCreateCoupon: false });
    }
  },

  createLPCoupon: async (coupon) => {
    set({ isLoadingCreateLPCoupon: true });

    if (!isString(coupon.image)) {
      try {
        const imgBase64: any = await toBase64(coupon.image);
        const res: any = await authRepository.uploadFile({
          name: coupon.image.name,
          base64: imgBase64,
        });
        coupon.image = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }
    const response = await couponsRepository.creteLPCoupon(coupon);

    if (response.isLeft()) {
      set({ isLoadingCreateLPCoupon: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingCreateLPCoupon: false });
    }
  },

  updateLPCoupon: async (coupon) => {
    set({ isLoadingUpdateLPCoupon: true });

    if (!isString(coupon.image)) {
      try {
        const imgBase64: any = await toBase64(coupon.image);
        const res: any = await couponsRepository.uploadFile({
          name: coupon.image.name,
          base64: imgBase64,
        });
        coupon.image = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }
    const response = await couponsRepository.updateLPCoupon(coupon);

    if (response.isLeft()) {
      set({ isLoadingUpdateLPCoupon: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingUpdateLPCoupon: false });
    }
  },

  approveLPCoupon: async (id) => {
    set({ isLoadingApproveLPCoupon: true });

    try {
      const response = await couponsRepository.approveLPCoupon(id);

      if (response.isLeft()) {
        set({ isLoadingApproveLPCoupon: false });
        throw new Error(response.value.message);
      } else {
        set({ isLoadingApproveLPCoupon: false });
      }
    } catch {
      set({ isLoadingApproveLPCoupon: false });
    }
  },

  refuseLPCoupon: async (id, comment) => {
    set({ isLoadingRefuseLPCoupon: true });

    try {
      const response = await couponsRepository.refuseLPCoupon(id, comment);

      if (response.isLeft()) {
        set({ isLoadingRefuseLPCoupon: false });
        throw new Error(response.value.message);
      } else {
        set({ isLoadingRefuseLPCoupon: false });
      }
    } catch {
      set({ isLoadingApproveLPCoupon: false });
    }
  },

  updateCoupon: async (coupon) => {
    set({ isLoadingUpdateCoupon: true });

    if (coupon.image && !isString(coupon.image)) {
      try {
        const imgBase64: any = await toBase64(coupon.image);
        const res: any = await couponsRepository.uploadFile({
          name: coupon.image.name,
          base64: imgBase64,
        });
        coupon.image = res.value.data.url;
      } catch (error) {
        console.error(error);
      }
    }
    const response = await couponsRepository.updateCoupon(coupon);

    if (response.isLeft()) {
      set({ isLoadingUpdateCoupon: false });
      throw new Error(response.value.message);
    } else {
      set({ isLoadingUpdateCoupon: false });
    }
  },

  exportData: async (ids: string[], userName: string, options) => {
    await advancedReportsRepository.exportCoupon(ids, userName, options);
  },

  getAllParkings: async () => {
    set({ isLoadingParkings: true });
    const response = await couponsRepository.getAllParkings();
    if (response.isLeft()) {
      set({ parkings: [], isLoadingParkings: false });
      return;
    }
    const { value } = response;
    const parkingsList = value.map((parking: any) => ({
      value: parking.code,
      label: parking.fulltag,
    }));
    set({
      parkings: parkingsList,
      isLoadingParkings: false,
    });
  },
}));
